import { useEffect, useMemo, useRef } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import {
  Content,
  EmptyState,
  Header,
  Link,
  MarkdownContent,
  Page,
  Progress,
  Table,
  TableColumn,
  WarningPanel,
} from '@backstage/core-components';
import { Box, Typography } from '@material-ui/core';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { relativeTime } from './relativeTime';
import { releaseDeployment, EntityDeploymentState } from './deploymentState';
import { ReleaseDeploymentChip } from './ReleaseDeploymentChip';
import { useDeploymentStates } from './useDeploymentStates';

type FeedRelease = {
  entityRef: string;
  tagName: string;
  name?: string;
  description?: string;
  commitSha?: string;
  releasedAt: string;
  webUrl?: string;
};

// How many of the newest releases start with their notes expanded, so the
// page reads like a changelog feed without opening rows by hand.
const AUTO_EXPANDED_RELEASES = 5;

const ReleaseNotes = ({ release }: { release: FeedRelease }) => (
  <Box px={3} py={2}>
    {release.description ? (
      <MarkdownContent content={release.description} linkTarget="_blank" />
    ) : (
      <Typography variant="body2" color="textSecondary">
        This release has no release notes.
      </Typography>
    )}
  </Box>
);

const columnsFor = (
  states: Record<string, EntityDeploymentState>,
  releasesByEntity: Map<string, FeedRelease[]>,
): TableColumn<FeedRelease>[] => [
  {
    title: 'Component',
    field: 'entityRef',
    render: release => <EntityRefLink entityRef={release.entityRef} />,
  },
  {
    title: 'Release',
    field: 'tagName',
    render: release => (
      <>
        {release.webUrl ? (
          <Link to={release.webUrl}>{release.tagName}</Link>
        ) : (
          release.tagName
        )}
        {release.name && release.name !== release.tagName
          ? ` ${release.name}`
          : ''}
      </>
    ),
  },
  {
    title: 'Released',
    field: 'releasedAt',
    defaultSort: 'desc',
    render: release => (
      <span title={new Date(release.releasedAt).toLocaleString()}>
        {relativeTime(release.releasedAt)}
      </span>
    ),
  },
  {
    // Not sortable or searchable: it is derived per row rather than a field on
    // the release, and material-table would sort the raw undefined values.
    title: 'Deployment',
    sorting: false,
    searchable: false,
    render: release => (
      <ReleaseDeploymentChip
        deployment={releaseDeployment({
          release,
          state: states[release.entityRef],
          releases: releasesByEntity.get(release.entityRef),
        })}
      />
    ),
  },
];

// Shared by the detailPanel prop and the imperative auto-expand toggle;
// material-table calls the former with { rowData } and the latter with the
// row itself, so accept both shapes.
const renderNotes = (row: FeedRelease | { rowData: FeedRelease }) => (
  <ReleaseNotes release={'rowData' in row ? row.rowData : row} />
);

export const ReleasesPage = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const tableRef = useRef<any>(null);
  const autoExpanded = useRef(false);

  const { value, loading, error } = useAsync(async (): Promise<
    FeedRelease[]
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('releases');
    const response = await fetch(`${baseUrl}/?limit=200&includeNotes=true`);
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error ??
          `Request failed with ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()).releases ?? [];
  }, []);

  // One request for every component on the page. Best-effort: no chips rather
  // than no releases when the cluster cannot be read.
  const { states } = useDeploymentStates(
    (value ?? []).map(release => release.entityRef),
  );

  // Each entity's own releases, so a superseded release can name the one that
  // replaced it rather than showing a bare revision.
  const releasesByEntity = useMemo(() => {
    const grouped = new Map<string, FeedRelease[]>();
    for (const release of value ?? []) {
      grouped.set(release.entityRef, [
        ...(grouped.get(release.entityRef) ?? []),
        release,
      ]);
    }
    return grouped;
  }, [value]);

  // Open the newest releases once the table has rendered its data, so the
  // first screen is immediately readable. Rows stay individually togglable.
  useEffect(() => {
    if (!value?.length || autoExpanded.current || !tableRef.current) {
      return;
    }
    autoExpanded.current = true;
    for (let i = 0; i < Math.min(AUTO_EXPANDED_RELEASES, value.length); i++) {
      tableRef.current.onToggleDetailPanel([i], renderNotes);
    }
  }, [value]);

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel title="Could not load releases" message={error.message} />
    );
  } else if (!value || value.length === 0) {
    content = (
      <EmptyState
        title="No releases yet"
        missing="content"
        description="No GitLab releases have been ingested yet. Releases appear here once the scheduled ingest has run."
      />
    );
  } else {
    content = (
      <Table<FeedRelease>
        title={`Latest releases (${value.length})`}
        options={{ paging: true, pageSize: 20, padding: 'dense' }}
        columns={columnsFor(states, releasesByEntity)}
        data={value}
        detailPanel={renderNotes}
        tableRef={tableRef}
      />
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title="Releases"
        subtitle="Everything released across the catalog, newest first"
      />
      <Content>{content}</Content>
    </Page>
  );
};
